import React, { useCallback } from 'react';
import { List, Typography } from 'antd';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import CombatantListItemContainer from './CombatantListItemContainer';
import type { Combatant } from '../../types/types';
import styles from './CombatantList.module.css';

const { Text } = Typography;

interface CombatantListProps {
  combatants: Combatant[];
  isActive: boolean;
  setupMode: boolean;
  turn: number;
  /** Pre-computed map of imageId -> imageUrl for combatant avatars */
  imageMap: Map<number, string>;
  /** Handler for reordering combatants via drag-and-drop (receives new ID order) */
  onReorder: (reorderedIds: number[]) => void;
}

/**
 * Displays the list of combatants with drag-and-drop reordering support.
 *
 * Pure presenter component (ADR-034): receives all data and callbacks as props.
 * ZERO store access.
 *
 * DndContext and SortableContext are UI infrastructure (not store access),
 * so they remain in the presenter. The drag-end handler computes the new
 * order locally and delegates to the onReorder callback.
 *
 * Each combatant row is rendered via CombatantListItemContainer, which owns
 * its own store connection for per-item mutations (HP, initiative, status effects).
 */
const CombatantList: React.FC<CombatantListProps> = ({
  combatants,
  isActive,
  setupMode,
  turn,
  imageMap,
  onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // Require a small drag before activating
    })
  );

  // Compute the new order on drag end and delegate to container via onReorder.
  // arrayMove is a pure utility from @dnd-kit/sortable, not store access.
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    // Only allow reordering in setup mode
    if (setupMode && over && active.id !== over.id) {
      const oldIndex = (combatants ?? []).findIndex(c => c.id === active.id);
      const newIndex = (combatants ?? []).findIndex(c => c.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrderedCombatants = arrayMove(combatants, oldIndex, newIndex);
      onReorder((newOrderedCombatants ?? []).map(c => Number(c.id)));
    }
  }, [setupMode, combatants, onReorder]);

  // Filter out any undefined or invalid combatants to prevent runtime errors
  const validCombatants = (combatants ?? []).filter(
    (c): c is Combatant => c?.id !== undefined && c.id !== null
  );

  if (validCombatants.length === 0 && !setupMode) {
    return (
      <div className={styles.placeholder}>
        <Text type="secondary">No combatants. Click "Setup Combat" to begin!</Text>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={(validCombatants ?? []).map(c => String(c.id))} strategy={verticalListSortingStrategy}>
        <List
          itemLayout="horizontal"
          dataSource={validCombatants}
          renderItem={(combatant, index) => (
            <CombatantListItemContainer
              key={combatant.id}
              combatant={combatant}
              isActiveTurn={isActive && index === turn}
              isNextTurn={isActive && index === (turn + 1) % validCombatants.length}
              setupMode={setupMode}
              imageMap={imageMap}
            />
          )}
        />
      </SortableContext>
    </DndContext>
  );
};

export default CombatantList;